import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
    lucideBell,
    lucideBuilding,
    lucideBuilding2,
    lucideClock,
    lucideExternalLink,
    lucideFileSpreadsheet,
    lucideKey,
    lucideLayoutGrid,
    lucideLock,
    lucideMail,
    lucideMenu,
    lucidePin,
    lucidePinOff,
    lucideSettings,
    lucideUser,
    lucideUsers
} from '@ng-icons/lucide';
import { filter } from 'rxjs';
import { Company } from '../../../auth/models/company/company';
import { User } from '../../../auth/models/user/user';
import { BreadcrumbCrumb, SETTINGS_LANDING_ROUTE, SettingsMenuItem, findCurrentPageCrumbs, settingsMenuGroups, visibleSettingsGroups } from './settings-menu.config';

const PIN_STORAGE_KEY = 'settings_shell_pinned';

function cleanUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
}

export function urlUsesSettingsShell(url: string): boolean {
    const clean = cleanUrl(url);
    if (clean === SETTINGS_LANDING_ROUTE || clean.startsWith(SETTINGS_LANDING_ROUTE + '/')) {
        return true;
    }
    for (const group of settingsMenuGroups) {
        for (const item of group.items) {
            if (clean === item.route || clean.startsWith(item.route + '/')) {
                return true;
            }
        }
    }
    return false;
}

interface ResolvedItem extends SettingsMenuItem {
    disabledReason: string | null;
}

interface ResolvedGroup {
    title: string;
    items: ResolvedItem[];
}

@Component({
    selector: 'jft-settings-shell',
    standalone: true,
    imports: [CommonModule, RouterLink, RouterLinkActive, NgIconComponent],
    templateUrl: './settings-shell.component.html',
    styleUrl: './settings-shell.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    viewProviders: [
        provideIcons({
            lucideBell,
            lucideBuilding,
            lucideBuilding2,
            lucideClock,
            lucideExternalLink,
            lucideFileSpreadsheet,
            lucideKey,
            lucideLayoutGrid,
            lucideLock,
            lucideMail,
            lucideMenu,
            lucidePin,
            lucidePinOff,
            lucideSettings,
            lucideUser,
            lucideUsers
        })
    ]
})
export class SettingsShellComponent {
    public user = inject(User);
    public company = inject(Company);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);

    public pinned = localStorage.getItem(PIN_STORAGE_KEY) === 'true';
    public hovered = false;
    public expanded = this.pinned;
    public visible = this.shouldShow(this.router.url);
    public pageCrumbs: BreadcrumbCrumb[] = findCurrentPageCrumbs(this.router.url);
    public onLandingPage = cleanUrl(this.router.url) === SETTINGS_LANDING_ROUTE;
    public landingRoute = SETTINGS_LANDING_ROUTE;
    public groups: ResolvedGroup[] = [];

    constructor() {
        this.groups = this.buildGroups();
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
            .subscribe(e => {
                const url = e.urlAfterRedirects;
                this.visible = this.shouldShow(url);
                this.pageCrumbs = findCurrentPageCrumbs(url);
                this.onLandingPage = cleanUrl(url) === SETTINGS_LANDING_ROUTE;
                this.syncBodyClass();
            });
        this.syncBodyClass();
    }

    private shouldShow(url: string): boolean {
        return this.user.isAuthenticated() && urlUsesSettingsShell(url);
    }

    public onHover(state: boolean): void {
        this.hovered = state;
        this.expanded = this.pinned || this.hovered;
    }

    public togglePin(): void {
        this.pinned = !this.pinned;
        this.expanded = this.pinned || this.hovered;
        localStorage.setItem(PIN_STORAGE_KEY, String(this.pinned));
    }

    private buildGroups(): ResolvedGroup[] {
        return visibleSettingsGroups(this.user, this.company)
            .map(({ group, items }) => ({
                title: group.title,
                items: items.map(i => ({
                    ...i,
                    disabledReason: i.disabled?.(this.user, this.company)?.reason ?? null
                }))
            }));
    }

    private syncBodyClass(): void {
        document.body.classList.toggle('has-settings-shell', this.visible);
    }

    public trackGroup = (_: number, g: ResolvedGroup) => g.title;
    public trackItem = (_: number, i: ResolvedItem) => i.route;
}
